import { ChevronDown, ChevronLeft, ChevronRight, Menu } from 'lucide-react';
import { useEffect, useState, type PropsWithChildren, type ReactNode } from 'react';

export type SidebarLinkItem = { type?: 'link'; href: string; label: string; icon: ReactNode; current?: boolean };
export type SidebarGroupChild = { href: string; label: string; icon: ReactNode; current?: boolean };
export type SidebarGroupItem = {
  type: 'group'; label: string; icon: ReactNode;
  children: SidebarGroupChild[];
};
export type SidebarItem = SidebarLinkItem | SidebarGroupItem;

const expandedGroupsKey = 'boothiva-sidebar-expanded';
function readExpandedGroups(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try { return JSON.parse(window.localStorage.getItem(expandedGroupsKey) ?? '{}'); } catch { return {}; }
}

/** Expanded (non-collapsed sidebar) rendering: an inline expand/collapse disclosure list. */
function SidebarGroup({ item }: { item: SidebarGroupItem }) {
  const groupActive = item.children.some((child) => child.current);
  const [expanded, setExpanded] = useState(() => readExpandedGroups()[item.label] ?? groupActive);
  useEffect(() => { if (groupActive) setExpanded(true); }, [groupActive]);
  const toggle = () => {
    setExpanded((value) => {
      const next = !value;
      const stored = readExpandedGroups();
      stored[item.label] = next;
      window.localStorage.setItem(expandedGroupsKey, JSON.stringify(stored));
      return next;
    });
  };
  return <div className="bv-sidebar__group">
    <button type="button" className={`bv-sidebar__link bv-sidebar__group-trigger${groupActive ? ' bv-sidebar__link--active' : ''}`}
      aria-expanded={expanded} onClick={toggle} title={item.label}>
      {item.icon}<span className="bv-sidebar__label">{item.label}</span>
      <ChevronDown aria-hidden="true" size={16} className={`bv-sidebar__chevron${expanded ? ' bv-sidebar__chevron--open' : ''}`} />
    </button>
    {expanded && <div className="bv-sidebar__group-children">
      {item.children.map((child) => (
        <a className="bv-sidebar__link bv-sidebar__link--child" href={child.href} key={child.href} aria-current={child.current ? 'page' : undefined} title={child.label}>
          {child.icon}<span className="bv-sidebar__label">{child.label}</span>
        </a>
      ))}
    </div>}
  </div>;
}

/** Collapsed (icon-only) sidebar rendering: a floating flyout panel on hover/focus/click, so the
 * submenu stays reachable instead of disappearing along with the text labels. */
function SidebarGroupCollapsed({ item }: { item: SidebarGroupItem }) {
  const groupActive = item.children.some((child) => child.current);
  const [open, setOpen] = useState(false);
  return <div className="bv-sidebar__group bv-sidebar__group--collapsed"
    onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
    <button type="button" className={`bv-sidebar__link bv-sidebar__group-trigger${groupActive ? ' bv-sidebar__link--active' : ''}`}
      aria-expanded={open} title={item.label}
      onClick={() => setOpen((value) => !value)} onFocus={() => setOpen(true)}>
      {item.icon}<span className="bv-sidebar__label">{item.label}</span>
    </button>
    {/* A disclosure of ordinary navigation links, not an ARIA menu: a menu promises arrow-key navigation and
        takes links out of the normal tab order, and this flyout offers neither. */}
    {open && <div className="bv-sidebar__flyout" role="group" aria-label={item.label} onMouseLeave={() => setOpen(false)}>
      <p className="bv-sidebar__flyout-title">{item.label}</p>
      {item.children.map((child) => (
        <a className={`bv-sidebar__flyout-link${child.current ? ' bv-sidebar__flyout-link--active' : ''}`} href={child.href} key={child.href}
          aria-current={child.current ? 'page' : undefined} onClick={() => setOpen(false)}>
          {child.icon}<span>{child.label}</span>
        </a>
      ))}
    </div>}
  </div>;
}

/** `footer` pins content to the bottom of the sidebar — a help card, a plan summary. Hidden while the
 *  sidebar is collapsed to icons, where there is no room for prose. */
export function Sidebar({ items, brand = 'Boothiva', footer }: { items: SidebarItem[]; brand?: ReactNode; footer?: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const toggle = () => setOpen((value) => !value);
    window.addEventListener('boothiva:toggle-sidebar', toggle);
    return () => window.removeEventListener('boothiva:toggle-sidebar', toggle);
  }, []);
  return (
    <aside className={`bv-sidebar${collapsed ? ' bv-sidebar--collapsed' : ''}${open ? ' bv-sidebar--open' : ''}`} aria-label="Primary navigation">
      <div className="bv-sidebar__head">
        <span className="bv-brand">{brand}</span>
        <button className="bv-button bv-button--quiet bv-icon-button" type="button" onClick={() => {
          setCollapsed((value) => !value); setOpen(false);
        }} aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}>
          {collapsed ? <ChevronRight aria-hidden="true" size={18} /> : <ChevronLeft aria-hidden="true" size={18} />}
        </button>
      </div>
      <nav className="bv-sidebar__nav">
        {items.map((item) => item.type === 'group'
          ? (collapsed ? <SidebarGroupCollapsed item={item} key={item.label} /> : <SidebarGroup item={item} key={item.label} />)
          : <a className="bv-sidebar__link" href={item.href} key={item.href} aria-current={item.current ? 'page' : undefined} title={item.label}>
              {item.icon}<span className="bv-sidebar__label">{item.label}</span>
            </a>)}
      </nav>
      {footer && !collapsed && <div className="bv-sidebar__footer">{footer}</div>}
    </aside>
  );
}

export function Topbar({ breadcrumbs, actions, admin = false }: { breadcrumbs?: ReactNode; actions?: ReactNode; admin?: boolean }) {
  return (
    <header className="bv-topbar">
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
        <button className="bv-button bv-button--quiet bv-icon-button bv-mobile-menu" type="button"
          onClick={() => window.dispatchEvent(new Event('boothiva:toggle-sidebar'))} aria-label="Open navigation">
          <Menu aria-hidden="true" size={18} />
        </button>
        {breadcrumbs}
        {admin && <span className="bv-admin-mark">Admin</span>}
      </div>
      <div style={{ alignItems: 'center', display: 'flex', gap: '.5rem' }}>
        {actions}
      </div>
    </header>
  );
}

/** `footer` sits below the page content, inside the scrolling area rather than pinned to the viewport, so a
 *  long page ends with it instead of carrying it along. */
export function AppShell({ sidebar, topbar, footer, children }: PropsWithChildren<{ sidebar: ReactNode; topbar: ReactNode; footer?: ReactNode }>) {
  return <div className="bv-app-shell">{sidebar}{topbar}
    <main className="bv-main" id="main-content">{children}{footer && <footer className="bv-app-footer">{footer}</footer>}</main>
  </div>;
}

export type BreadcrumbItem = { label: string; href?: string };
export function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  return <nav className="bv-breadcrumbs" aria-label="Breadcrumb"><ol>{items.map((item, index) => (
    <li key={`${item.label}-${index}`} aria-current={index === items.length - 1 ? 'page' : undefined}>
      {item.href && index < items.length - 1 ? <a href={item.href}>{item.label}</a> : item.label}
    </li>
  ))}</ol></nav>;
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return <div className="bv-page-header"><div><h1 className="bv-page-title">{title}</h1>{description && <p className="bv-muted">{description}</p>}</div>{actions && <div className="bv-page-header__actions">{actions}</div>}</div>;
}
